// NOTE: gasby-config cannot resolve `stile-shared' as a workspace package
// must use relative imports in this file!
import { BLOG_URL, PAGE_SECTIONS, SHOP_URL } from '../../stile-shared/src/constants';
import type { PageData } from '../../stile-shared/src/utils/getPageData';

export * from '../../stile-shared/src/constants';

export const SITE_LOCALE = 'en-au';

/**
 * This is to assist with keeping page data in various places of the app in sync
 * E.g. Hero & NextPage blocks, Header & Footer menus
 * One-off or unsectioned pages (e.g. home, /other/ ) don't really need to be added here,
 * their seo, hero data etc is kept within their page files.
 */
export const PAGE_DATA: PageData = {
  'what-is-stile': {
    'revision-and-mastery-tools': {
      seo: {
        title: 'New note-taking and study resources',
        description: 'Set your students up for success with scaffolded revision tools.',
      },
      hero: {
        title: 'New note-taking and study resources',
        text: 'Set your students up for success with scaffolded revision tools',
      },
      menu: {
        text: 'Revision and mastery tools',
        url: '/what-is-stile/revision-and-mastery-tools/',
      },
    },

    'whats-included': {
      seo: {
        title: "What's included?",
        description:
          'Stile is a complete, comprehensive core curriculum for high school. It’s battle-tested and highly refined, having been used (and loved!) by thousands of teachers for over 10 years.',
      },
      hero: {
        title: "What's included?",
        text: 'Stile is a complete, comprehensive core curriculum for high school. It’s battle-tested and highly refined, having been used (and loved!) by thousands of teachers for over 10 years.',
      },
      menu: {
        text: "What's included?",
        url: '/what-is-stile/whats-included/',
      },
    },

    'stile-x': {
      seo: {
        title: 'Stile X Workbooks: Hands-On Learning for Science Students',
        description:
          'Stile X Workbooks bring science lessons to life—on or off devices. With worksheets, glossaries, and practice tests, they help students make sense of science',
      },
      hero: {
        title: 'Stile X: Printed revision booklets',
        text: 'The ultimate student notebook and study guide',
      },
      menu: { text: 'Stile X: Student workbooks', url: '/what-is-stile/stile-x/' },
    },

    'professional-learning': {
      seo: {
        title: 'Professional Learning for Aus Teachers | Stile Academy',
        description:
          'Need to build your professional development? with Stile Academy. Complete PD hours, earn certifications, and evolve your teaching skills—aligned to your schedule.',
      },
      hero: {
        title: 'Integrated Professional Learning',
        text: 'Stile Professional Learning is about great science teaching',
      },
      menu: {
        text: 'Integrated Professional Learning',
        url: '/what-is-stile/professional-learning/',
      },
    },

    'stile-classroom': {
      seo: {
        title: 'Stile in the classroom',
        description: 'World-class resources for direct instruction and self-paced learning.',
      },
      hero: {
        title: 'Stile in the classroom',
        text: 'World-class resources for direct instruction and self-paced learning',
      },
      menu: { text: 'Stile in the classroom', url: '/what-is-stile/stile-classroom/' },
    },

    'responsive-support': {
      seo: {
        title: 'Responsive support: Personalised Guidance for Schools',
        description:
          'Expert science teachers providing strategic support and professional learning for educators and leaders.',
      },
      hero: {
        title: 'Responsive support',
        text: 'Expert guidance and support to help your team succeed with Stile',
      },
      menu: {
        text: 'Responsive support',
        url: '/what-is-stile/responsive-support/',
      },
    },

    'technology-for-teaching': {
      seo: {
        title: 'Technology for teaching',
        description: 'Stile is purpose-built to empower teaching and learning in the classroom',
      },
      hero: {
        title: 'Technology for teaching',
        text: 'Purpose-built to empower teaching and learning in the classroom',
      },
      menu: { text: 'Technology for teaching', url: '/what-is-stile/technology-for-teaching/' },
    },
  },

  'why-choose-stile': {
    benefits: {
      seo: {
        title: 'Benefits of using Stile',
        description: 'Experience the benefits of using Stile in the science classroom.',
      },
      hero: { title: 'Benefits', text: 'You’ll see the difference' },
      menu: { text: 'Benefits', url: '/why-choose-stile/benefits/' },
    },

    'high-quality-instructional-materials': {
      seo: {
        title: 'What are High-Quality Instructional Materials for Teachers? | Stile Education',
        description:
          'Teaching resources built on evidence-based research and HQIM approved. Create lessons that stick, engage students, and transform learning. Get the checklist now.',
      },
      hero: { title: 'High-quality instructional materials', text: '' },
      menu: {
        text: 'High-quality instructional materials',
        url: '/why-choose-stile/high-quality-instructional-materials/',
      },
    },

    'evidence-of-success': {
      seo: {
        title: 'Evidence of success',
        description: 'Hear from teachers who are seeing the difference.',
        uniqueToLocale: true,
      },
      hero: { title: 'Evidence of success', text: 'Teachers are seeing the difference' },
      menu: { text: 'Evidence of success', url: '/why-choose-stile/evidence-of-success/' },
    },

    'evidence-based-pedagogy': {
      seo: {
        title: 'Evidence-based pedagogy',
        description: 'Stile drives the adoption of evidence-based teaching strategies',
      },
      hero: {
        title: 'An evidence-based pedagogical model',
        text: 'Stile drives the adoption of evidence-based teaching strategies',
      },
      menu: {
        text: 'An evidence-based pedagogical model',
        url: '/why-choose-stile/evidence-based-pedagogy/',
      },
    },

    'high-impact-teaching-strategies': {
      seo: {
        title: 'High impact teaching strategies',
        description:
          'Stile is purpose-built to promote the use of evidence-based teaching strategies',
        uniqueToLocale: true,
      },
      hero: {
        title: 'High impact teaching strategies',
        text: 'Purpose-built to promote the use of evidence-based teaching strategies',
      },
      menu: {
        text: 'High impact teaching strategies',
        url: '/why-choose-stile/high-impact-teaching-strategies/',
      },
    },

    stem: {
      seo: {
        title: 'A true STEM education',
        description:
          'Connect the dots with Maths, Technology and Engineering, and help students get a true STEM education.',
      },
      hero: {
        title: 'A true STEM education',
        text: 'Join the dots with Maths, Technology and Engineering',
      },
      menu: { text: 'A true STEM education', url: '/why-choose-stile/stem/' },
    },

    steam: {
      seo: {
        title: 'STEAM: Art of Science',
        description: 'Celebrating the interplay between art and STEM',
        uniqueToLocale: true,
      },
      hero: {
        title: 'STEAM: Art of Science',
        text: 'Celebrating the interplay between art and STEM',
      },
      menu: { text: 'STEAM: Art of Science', url: '/why-choose-stile/steam/' },
    },

    'literacy-and-numeracy': {
      seo: {
        title: 'Literacy and numeracy',
        description: 'Stile reinforces core literacy and numeracy skills.',
        uniqueToLocale: true,
      },
      hero: {
        title: 'Boost literacy and numeracy',
        text: 'Stile reinforces core literacy and numeracy skills',
      },
      menu: {
        text: 'Boost literacy and numeracy',
        url: '/why-choose-stile/literacy-and-numeracy/',
      },
    },

    'what-teachers-are-saying': {
      seo: {
        title: 'Recommended by teachers',
        description:
          'Thousands of teachers use Stile every week. Learn more about their experience with Stile!',
      },
      hero: {
        title: 'Recommended by teachers',
        text: 'Thousands of teachers use Stile every week',
      },
      menu: { text: 'Recommended by teachers', url: '/why-choose-stile/what-teachers-are-saying/' },
    },
  },

  'who-we-are': {
    apply: {
      seo: {
        title: 'Apply for a job at Stile',
        description: 'Join the Stile team and improve the state of science education globally.',
        opengraphImageSelection: 'apply',
      },
      hero: { title: '', text: '' },
      menu: { text: '', url: '/who-we-are/apply/' },
    },

    'effective-stilists': {
      seo: {
        title: 'Effective Stilists',
        description: 'Stile team values',
      },
      hero: { title: 'Effective Stilists', text: 'Our team values' },
      menu: { text: 'Effective Stilists', url: '/who-we-are/effective-stilists/' },
    },

    'our-why': {
      seo: {
        title: 'Our why',
        description: 'A world-class science education for every student',
      },
      hero: { title: 'Our why', text: 'A world-class science education for every student' },
      menu: { text: 'Our why', url: '/who-we-are/our-why/' },
    },

    'our-principles': {
      seo: {
        title: 'Our guarantees and principles',
        description: 'The core beliefs that guide everything we do',
      },
      hero: {
        title: 'Our guarantees and principles',
        text: 'The core beliefs that guide everything we do',
      },
      menu: { text: 'Our guarantees and principles', url: '/who-we-are/our-principles/' },
    },

    'our-team': {
      seo: {
        title: 'Our team',
        description: 'We share a passion for great science education',
      },
      hero: { title: 'Our team', text: 'We share a passion for great science education' },
      menu: { text: 'Our team', url: '/who-we-are/our-team/' },
    },

    'join-the-team': {
      seo: {
        title: 'Join the team',
        description: 'Stile employees share a passion for great science education.',
        opengraphImageSelection: 'apply',
      },
      hero: { title: 'Join the team', text: 'We share a passion for great science education' },
      menu: { text: 'Join the team', url: '/who-we-are/join-the-team/' },
    },

    'gender-diversity-in-stem': {
      seo: {
        title: 'Celebrating gender diversity in STEM',
        description: 'Building a more equitable future.',
      },
      hero: {
        title: 'Celebrating gender diversity in STEM',
        text: 'Building a more equitable future',
      },
      menu: { text: 'Gender diversity in STEM', url: '/who-we-are/gender-diversity-in-stem/' },
    },

    'engineering-at-stile': {
      seo: {
        title: 'Engineering at Stile',
        description: 'Building tools that improve the state of science education globally.',
      },
      hero: {
        title: 'Being a software engineer at Stile',
        text: 'Build tools that improve the state of science education globally',
      },
      menu: { text: 'Engineering at Stile', url: '/who-we-are/engineering-at-stile/' },
    },

    'hiring-process': {
      seo: {
        title: 'Hiring process',
        description:
          'Stile aspires to build a team of exceptional individuals who are a genuine delight to work with every day.',
      },
      hero: { title: 'Hiring process', text: 'Building a team of exceptional individuals' },
      menu: { text: 'Hiring process', url: '/who-we-are/hiring-process/' },
    },

    'life-at-stile': {
      seo: {
        title: 'Life at Stile',
        description:
          'Stile is for those who want to make an impact and improve our education system. Learn more about our culture!',
      },
      hero: { title: 'Life at Stile', text: 'Spending time with our brilliant team' },
      menu: { text: 'Life at Stile', url: '/who-we-are/life-at-stile/' },
    },

    contact: {
      seo: {
        title: 'Contact us',
        description: "Stile's contact details",
      },
      hero: { title: 'Contact us', text: 'We’re here to help' },
      menu: { text: 'Contact us', url: '/who-we-are/contact/' },
    },
  },

  resources: {
    nsw: {
      seo: {
        title: 'Stile and the NSW Syllabus Changes',
        description: 'Implementing the new NSW science syllabus with Stile',
        uniqueToLocale: true,
      },
      hero: { title: 'The new NSW Syllabus', text: 'We’ve got you covered' },
      menu: { text: 'New NSW Syllabus Implementation', url: '/resources/nsw/' },
    },
    'victorian-curriculum': {
      seo: {
        title: 'Stile Education for the Victorian Curriculum',
        description:
          'Teaching science has never been easier. Get up-to-date, customisable lessons, hands-on activities, and teaching resources aligned to the Victorian curriculum (Years 7–10).',
        uniqueToLocale: true,
      },
      hero: {
        title: 'New VIC Curriculum 2.0 Implementation',
        text: '',
      },
      menu: {
        text: 'New VIC Curriculum 2.0 Implementation',
        url: '/resources/victorian-curriculum/',
      },
    },
    'stile-blog': {
      seo: { title: '', description: '' },
      hero: { text: '', title: '' },
      menu: { text: 'Blog', url: 'https://blog.stileeducation.com/' },
    },
    'stile-shop': {
      seo: { title: '', description: '' },
      hero: { text: '', title: '' },
      menu: { text: 'Stile Shop', url: 'https://shop.stileeducation.com/' },
    },
    'parents-and-guardians': {
      seo: {
        title: 'The perfect resource for your child',
        description:
          "Helping young citizens graduate from school ready to tackle tomorrow's problems",
      },
      hero: {
        title: 'The perfect resource for your child',
        text: "Helping young citizens graduate from school ready to tackle tomorrow's problems",
      },
      menu: { text: 'For parents and guardians', url: '/resources/parents-and-guardians/' },
    },
    'planning-documents': {
      seo: {
        title: 'Curriculum planning documents',
        description: 'Get a head start on planning with our curriculum-aligned scope and sequences',
      },
      hero: {
        title: 'Curriculum planning documents',
        text: 'Get a head start on planning with our curriculum-aligned scope and sequences',
      },
      menu: {
        text: 'Curriculum planning documents',
        url: '/resources/planning-documents/',
      },
    },
    'case-studies': {
      seo: {
        title: 'Case studies',
        description: 'Hear directly from science teachers and leaders on the benefits of Stile',
      },
      hero: {
        title: 'Case studies',
        text: 'Hear directly from science teachers and leaders on the benefits of Stile',
      },
      menu: {
        text: 'Case studies',
        url: '/resources/case-studies/',
      },
    },
    'ict-professionals': {
      seo: {
        title: 'High-quality resources built on a secure, flexible platform',
        description: 'Your security and privacy needs, covered',
      },
      hero: {
        title: 'High-quality resources built on a secure, flexible platform',
        text: 'Your security and privacy needs, covered',
      },
      menu: {
        text: 'For ICT professionals',
        url: '/resources/ict-professionals/',
      },
    },
    'lunaria-one': {
      seo: {
        title: "Stile's partnership with Lunaria One",
        description: 'Partnering to answer the question: Can plants grow on the Moon?',
      },
      hero: {
        title: 'Can plants grow on the Moon?',
        text: 'Stile is exclusively partnering with Lunaria One for an out-of-this-world mission',
      },
      menu: { text: 'Lunaria One Partnership', url: '/resources/lunaria-one/' },
    },
  },
};

/* These can vary between locales */
export const HEADER_LINK_SECTIONS = [
  {
    title: PAGE_SECTIONS['what-is-stile'],
    links: [
      PAGE_DATA['what-is-stile']['whats-included'].menu,
      PAGE_DATA['what-is-stile']['stile-classroom'].menu,
      PAGE_DATA['what-is-stile']['stile-x'].menu,
      PAGE_DATA['what-is-stile']['professional-learning'].menu,
      PAGE_DATA['what-is-stile']['technology-for-teaching'].menu,
      PAGE_DATA['what-is-stile']['responsive-support'].menu,
    ],
  },
  {
    title: PAGE_SECTIONS['why-choose-stile'],
    links: [
      PAGE_DATA['why-choose-stile']['benefits'].menu,
      PAGE_DATA['why-choose-stile']['high-quality-instructional-materials'].menu,
      PAGE_DATA['why-choose-stile']['evidence-of-success'].menu,
      PAGE_DATA['why-choose-stile']['evidence-based-pedagogy'].menu,
      PAGE_DATA['why-choose-stile']['stem'].menu,
      PAGE_DATA['why-choose-stile']['steam'].menu,
      PAGE_DATA['why-choose-stile']['literacy-and-numeracy'].menu,
      PAGE_DATA['why-choose-stile']['what-teachers-are-saying'].menu,
    ],
  },
  {
    title: PAGE_SECTIONS['who-we-are'],
    links: [
      PAGE_DATA['who-we-are']['our-why'].menu,
      PAGE_DATA['who-we-are']['our-principles'].menu,
      PAGE_DATA['who-we-are']['our-team'].menu,
      PAGE_DATA['who-we-are']['join-the-team'].menu,
      PAGE_DATA['who-we-are']['gender-diversity-in-stem'].menu,
      PAGE_DATA['who-we-are']['contact'].menu,
    ],
  },
  {
    title: PAGE_SECTIONS['resources'],
    links: [
      PAGE_DATA['resources']['nsw'].menu,
      PAGE_DATA['resources']['victorian-curriculum'].menu,
      PAGE_DATA['resources']['planning-documents'].menu,
      PAGE_DATA['resources']['lunaria-one'].menu,
      PAGE_DATA['resources']['case-studies'].menu,
      PAGE_DATA['resources']['stile-blog'].menu,
      PAGE_DATA['resources']['stile-shop'].menu,
      PAGE_DATA['resources']['ict-professionals'].menu,
      PAGE_DATA['resources']['parents-and-guardians'].menu,
    ],
  },
];

/* These can vary between locales */
export const FOOTER_LINK_SECTIONS = [
  {
    title: PAGE_SECTIONS['what-is-stile'],
    links: [
      PAGE_DATA['what-is-stile']['stile-classroom'].menu,
      PAGE_DATA['what-is-stile']['stile-x'].menu,
      PAGE_DATA['what-is-stile']['technology-for-teaching'].menu,
      PAGE_DATA['what-is-stile']['professional-learning'].menu,
      PAGE_DATA['what-is-stile']['responsive-support'].menu,
    ],
  },
  {
    title: PAGE_SECTIONS['why-choose-stile'],
    links: [
      PAGE_DATA['why-choose-stile']['benefits'].menu,
      PAGE_DATA['why-choose-stile']['high-quality-instructional-materials'].menu,
      PAGE_DATA['why-choose-stile']['evidence-of-success'].menu,
      PAGE_DATA['why-choose-stile']['evidence-based-pedagogy'].menu,
      PAGE_DATA['why-choose-stile']['high-impact-teaching-strategies'].menu,
      PAGE_DATA['why-choose-stile']['stem'].menu,
      PAGE_DATA['why-choose-stile']['steam'].menu,
      PAGE_DATA['why-choose-stile']['literacy-and-numeracy'].menu,
      PAGE_DATA['why-choose-stile']['what-teachers-are-saying'].menu,
    ],
  },
  {
    title: PAGE_SECTIONS['who-we-are'],
    links: [
      PAGE_DATA['who-we-are']['our-why'].menu,
      PAGE_DATA['who-we-are']['our-principles'].menu,
      PAGE_DATA['who-we-are']['our-team'].menu,
      PAGE_DATA['who-we-are']['join-the-team'].menu,
      PAGE_DATA['who-we-are']['gender-diversity-in-stem'].menu,
      PAGE_DATA['who-we-are']['engineering-at-stile'].menu,
      PAGE_DATA['who-we-are']['hiring-process'].menu,
      PAGE_DATA['who-we-are']['life-at-stile'].menu,
      PAGE_DATA['who-we-are']['contact'].menu,
    ],
  },
  {
    title: PAGE_SECTIONS['resources'],
    links: [
      PAGE_DATA['resources']['nsw'].menu,
      PAGE_DATA['resources']['victorian-curriculum'].menu,
      PAGE_DATA['resources']['planning-documents'].menu,
      PAGE_DATA['resources']['lunaria-one'].menu,
      PAGE_DATA['resources']['case-studies'].menu,
      PAGE_DATA['resources']['stile-blog'].menu,
      PAGE_DATA['resources']['stile-shop'].menu,
      PAGE_DATA['resources']['ict-professionals'].menu,
      PAGE_DATA['resources']['parents-and-guardians'].menu,
    ],
  },
];

export const OTHER_SITE_LINKS = [
  { text: 'Privacy', url: '/other/privacy/' },
  { text: 'Security', url: '/other/security/' },
  { text: 'Terms', url: '/other/terms/' },
  { text: 'Blog', url: BLOG_URL },
  { text: 'Resources', url: '/resources/' },
  { text: 'Shop', url: SHOP_URL },
];
